import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeature } from '../hooks/useFeature';
import { getConnectionStatus, subscribeConnection } from '../utils/connectionStatus';
import { SIGNER_ROLES, SIGNER_ROLE_LABELS, errorMessage, hasRequiredDocuments } from '../utils/staffDocuments';
import AddDocument from '../components/staffDocuments/AddDocument';
import './booking/booking-shared.css';
import './StaffDocuments.css';

const rolesLabel = roles => (roles.length === SIGNER_ROLES.length ? 'Everyone on staff' : roles.map(r => SIGNER_ROLE_LABELS[r]).join(', '));

// Admin, Safety: the documents staff read and sign, such as the code of
// conduct. Club admins add documents and see how many have signed each one.
export default function StaffDocuments() {
  const { user, can } = useAuth();
  // Staff who manage staff documents add, edit, archive and see who has signed
  const managesDocuments = can('staffDocuments', 'MANAGE');
  const navigate = useNavigate();
  const canAdd = useFeature('staffDocuments');
  const [docs, setDocs] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [online, setOnline] = useState(() => getConnectionStatus() !== 'offline');
  useEffect(() => subscribeConnection(s => setOnline(s !== 'offline')), []);

  // Until a club requires a document, only staff with staff documents access see them
  const allowed = can('staffDocuments') || hasRequiredDocuments(user);
  useEffect(() => {
    if (!allowed) return;
    axios.get('/api/staff-documents', { params: showArchived ? { archived: true } : undefined })
      .then(res => { setDocs(res.data); setLoadError(null); })
      .catch(err => setLoadError(errorMessage(err, 'Could not load the staff documents.')));
  }, [allowed, showArchived]);

  if (!allowed) return <Navigate to="/dashboard" replace />;

  return (
    <div className="bk-page coc-page">
      <h2>Staff documents</h2>
      <p className="text-muted">Documents staff read and sign, like the code of conduct.</p>
      {loadError && <p className="bk-error">{loadError}</p>}
      {!docs && !loadError && <p className="text-muted">Loading…</p>}
      {docs && docs.length === 0 && <p>No documents yet.</p>}
      {docs && docs.length > 0 && (
        <ul className="coc-list">
          {docs.map(d => (
            <li key={d.id} className="bk-card coc-list__item">
              <div className="coc-list__main">
                <Link to={`/staff-documents/${d.id}`} className="coc-list__title">{d.title}</Link>
                <span className="text-muted">
                  {d.kind === 'FILE' ? 'PDF' : 'Written'}
                  {d.versionLabel ? `, ${d.versionLabel}` : ''}
                  {managesDocuments ? `. Signed by ${rolesLabel(d.signerRoles)}` : ''}
                </span>
                {managesDocuments && d.toSignCount != null && (
                  <span className="text-muted">{d.signedCount} of {d.toSignCount} signed</span>
                )}
              </div>
              <div className="coc-list__badges">
                {d.archived && <span className="coc-badge">Archived</span>}
                {d.required && <span className="coc-badge coc-badge--required">Required</span>}
                {!d.archived && (d.signed
                  ? <span className="coc-badge coc-badge--signed">Signed</span>
                  : SIGNER_ROLES.includes(user?.role) && d.signerRoles.includes(user.role) && <span className="coc-badge coc-badge--todo">To sign</span>)}
              </div>
            </li>
          ))}
        </ul>
      )}
      {managesDocuments && (
        <label className="coc-require coc-list__archived">
          <input type="checkbox" className="bk-checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
          Show archived
        </label>
      )}
      {managesDocuments && canAdd && (
        <AddDocument online={online} onCreated={doc => navigate(`/staff-documents/${doc.id}`)} />
      )}
    </div>
  );
}
